import { useState } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
    useCohortRetentionWeeks,
    type CohortActivity,
    type CohortMetricType,
    type CohortWeekRetention,
} from '@/hooks/useAdminAnalytics';

const WEEK_COLUMNS: { key: keyof CohortWeekRetention; label: string }[] = [
    { key: 'w0_pct', label: 'W0' },
    { key: 'w1_pct', label: 'W1' },
    { key: 'w2_pct', label: 'W2' },
    { key: 'w3_pct', label: 'W3' },
    { key: 'w4_pct', label: 'W4' },
    { key: 'w6_pct', label: 'W6' },
    { key: 'w8_pct', label: 'W8' },
    { key: 'w12_pct', label: 'W12' },
];

const ACTIVITIES: { value: CohortActivity; label: string }[] = [
    { value: 'session', label: 'Sesión' },
    { value: 'capture', label: 'Captura' },
    { value: 'feed', label: 'Feed' },
    { value: 'any', label: 'Cualquiera' },
];

interface CohortWeeksTableProps {
    metricType: CohortMetricType;
}

export function CohortWeeksTable({ metricType }: CohortWeeksTableProps) {
    const [activity, setActivity] = useState<CohortActivity>('session');
    const { data, isLoading, isError, error, refetch, isFetching } = useCohortRetentionWeeks(
        metricType,
        activity,
    );
    const rows = (data ?? []).slice(0, 16);
    const matured = rows.filter((row) => row.weeks_0_pct != null);

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-muted-foreground">Volvió con</span>
                {ACTIVITIES.map((item) => (
                    <button
                        key={item.value}
                        type="button"
                        onClick={() => setActivity(item.value)}
                        className={cn(
                            'px-3 py-1.5 rounded-md text-xs font-medium transition-all',
                            activity === item.value
                                ? 'bg-primary text-primary-foreground shadow-sm'
                                : 'bg-muted/50 text-muted-foreground hover:text-foreground',
                        )}
                    >
                        {item.label}
                    </button>
                ))}
            </div>
            <p className="text-xs text-muted-foreground max-w-3xl" title="Europe/Madrid, semana lunes-domingo">
                Wk es quien tuvo al menos un evento de ese tipo en la semana k después de la semana de alta
                (lunes a domingo, hora de Madrid). W0 es la propia semana de alta y puede ser menor que 100%.
                Una semana que aún no ha terminado se muestra como —. Cambiar el evento no cambia el tamaño de la cohorte.
                Últimos 150 días, para que W8 y W12 hayan podido cerrar.
            </p>

            <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-border bg-muted/30">
                                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase">Semana</th>
                                <th className="text-center px-3 py-3 text-xs font-semibold text-muted-foreground uppercase">n</th>
                                {WEEK_COLUMNS.map((col) => (
                                    <th key={col.key} className="text-center px-3 py-3 text-xs font-semibold text-muted-foreground uppercase">
                                        {col.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={10} className="py-12 text-center text-muted-foreground">
                                        <RefreshCw size={20} className="animate-spin mx-auto mb-2" />
                                        Cargando cohortes semanales...
                                    </td>
                                </tr>
                            ) : isError ? (
                                <tr>
                                    <td colSpan={10} className="py-8 text-center text-sm text-rose-400">
                                        {(error as Error)?.message || 'No se pudo cargar la retención semanal'}
                                        <button type="button" onClick={() => refetch()} className="ml-2 underline text-primary">
                                            {isFetching ? 'Reintentando…' : 'Reintentar'}
                                        </button>
                                    </td>
                                </tr>
                            ) : rows.length === 0 ? (
                                <tr>
                                    <td colSpan={10} className="py-12 text-center text-muted-foreground">
                                        No hay cohortes en los últimos 150 días
                                    </td>
                                </tr>
                            ) : rows.map((row) => {
                                const weekStart = new Date(`${row.cohort_date}T12:00:00`);
                                return (
                                    <tr key={row.cohort_date} className="hover:bg-muted/20">
                                        <td className="px-4 py-2 text-xs">
                                            <p className="font-medium text-foreground">{format(weekStart, 'dd/MM', { locale: es })}</p>
                                            <p className="text-muted-foreground">{format(weekStart, 'MMM yy', { locale: es })}</p>
                                        </td>
                                        <td className="px-3 py-2 text-center font-bold text-foreground">{row.initial_count}</td>
                                        {WEEK_COLUMNS.map((col) => (
                                            <WeekCell key={col.key} pct={row[col.key] as number | null} />
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {matured.length > 0 && (
                <div className="rounded-xl border border-border bg-card overflow-hidden">
                    <div className="px-4 py-3 border-b border-border">
                        <h3 className="text-sm font-semibold text-foreground">Semanas con actividad, de las 8 primeras</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            Solo cohortes cuya semana 7 ya cerró. 0, 1, 2, 3-4 y 5+ suman el 100% de la cohorte.
                            «Seguidas» tuvo al menos dos semanas consecutivas. «Con hueco» tuvo dos o más, ninguna seguida.
                        </p>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-border bg-muted/30 text-xs text-muted-foreground">
                                    <th className="text-left px-4 py-2 font-semibold">Semana</th>
                                    <th className="text-center px-2 py-2 font-semibold">n</th>
                                    <th className="text-center px-2 py-2 font-semibold">0</th>
                                    <th className="text-center px-2 py-2 font-semibold">1</th>
                                    <th className="text-center px-2 py-2 font-semibold">2</th>
                                    <th className="text-center px-2 py-2 font-semibold">3-4</th>
                                    <th className="text-center px-2 py-2 font-semibold">5+</th>
                                    <th className="text-center px-2 py-2 font-semibold">Seguidas</th>
                                    <th className="text-center px-2 py-2 font-semibold">Con hueco</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {matured.map((row) => (
                                    <tr key={`dist-${row.cohort_date}`}>
                                        <td className="px-4 py-2 text-xs font-medium">{row.cohort_date.slice(8, 10)}/{row.cohort_date.slice(5, 7)}</td>
                                        <td className="px-2 py-2 text-center text-xs">{row.initial_count}</td>
                                        <WeekCell pct={row.weeks_0_pct} />
                                        <WeekCell pct={row.weeks_1_pct} />
                                        <WeekCell pct={row.weeks_2_pct} />
                                        <WeekCell pct={row.weeks_3_4_pct} />
                                        <WeekCell pct={row.weeks_5plus_pct} />
                                        <WeekCell pct={row.consecutive_pct} />
                                        <WeekCell pct={row.gapped_pct} />
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

function WeekCell({ pct }: { pct: number | null }) {
    if (pct == null) {
        return (
            <td className="px-2 py-2 text-center">
                <span className="text-xs text-muted-foreground/50" title="Semana aún no cerrada, o sin cohorte">—</span>
            </td>
        );
    }
    const bg = pct >= 50 ? 'bg-emerald-500'
        : pct >= 25 ? 'bg-amber-400'
            : pct > 0 ? 'bg-orange-400'
                : 'bg-muted/50';
    return (
        <td className="px-2 py-2 text-center">
            <div className="inline-flex items-center gap-1">
                <div className={cn('w-6 h-3 rounded', bg)} />
                <span className="text-xs font-medium text-foreground min-w-[36px]">{pct.toFixed(1)}%</span>
            </div>
        </td>
    );
}
